import { api } from '../../lib/api';
import { day, number } from '../../lib/format';
import { Card, Empty, ErrorNote, Loading, Stat, useResource } from '../../components/ui';
import { pct, plural, type CookieDashboard, type View } from './types';

/** The cookie dashboard: figures, banner choices over the period, and where cookies were found. */
export default function Overview({ days, reload, go }: { days: number; reload: number; go: (v: View) => void }) {
  const { data, error, loading } = useResource<CookieDashboard>(
    () => api.get(`/v1/cookies/dashboard?days=${days}`), [days, reload],
  );

  if (error) return <ErrorNote error={error} />;
  if (loading && !data) return <Loading what="Loading cookie dashboard" />;
  if (!data) return null;

  const t = data.totals;
  const choiceTotals = data.choices.reduce(
    (a, c) => ({ allowed: a.allowed + c.allowed, refused: a.refused + c.refused, withdrawn: a.withdrawn + c.withdrawn }),
    { allowed: 0, refused: 0, withdrawn: 0 },
  );

  return (
    <>
      <div className="ck-figures">
        <Stat label="Declared" value={<>{number(t.confirmed)}<span className="ck-of"> of {number(t.cookies)}</span></>} />
        <Stat label="Waiting for a decision" value={number(t.waiting)} tone={t.waiting ? 'warn' : undefined} />
        {t.beforeConsent !== undefined && (
          <Stat label="Set before consent" value={number(t.beforeConsent)} tone={t.beforeConsent ? 'bad' : undefined} />
        )}
        <Stat label="Third party" value={number(t.thirdParty)} />
        <Stat label="Vendors" value={number(t.vendors)} />
        <Stat label="Open alerts" value={number(data.openAlerts)} tone={data.openAlerts ? 'bad' : undefined} />
      </div>

      <p className="ck-facts">
        <span>Last scan {data.lastScan ? <>{day(data.lastScan.at)} · {plural(data.lastScan.newFindings, 'new cookie')}</> : 'none'}</span>
        <span>Next scheduled scan {data.schedules.nextRunAt ? day(data.schedules.nextRunAt) : 'none'}</span>
        <span>Policy {data.policy ? <>version {data.policy.version} · {day(data.policy.publishedAt)}</> : 'not published'}</span>
        {(t.waiting > 0 || (t.beforeConsent ?? 0) > 0) && <button className="ck-link" onClick={() => go('cookies')}>Review cookies</button>}
      </p>

      <div className="grid cols-2 ck-grid">
        <Card title={`Banner choices · ${days} days`}>
          <ChoiceTrend rows={data.trend} />
        </Card>

        <Card title="Choices by purpose">
          {data.choices.length === 0 ? <Empty>No banner choices in this period.</Empty> : (
            <div className="table-scroll">
              <table className="data ck-table">
                <thead>
                  <tr>
                    <th>Purpose</th>
                    <th className="num">Allowed</th>
                    <th className="num">Refused</th>
                    <th className="num">Withdrawn</th>
                    <th className="num">Allowed %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.choices.map((c) => (
                    <tr key={c.purposeId}>
                      <td>{c.purpose}</td>
                      <td className="num">{number(c.allowed)}</td>
                      <td className="num">{number(c.refused)}</td>
                      <td className="num">{number(c.withdrawn)}</td>
                      <td className="num">{pct(c.allowed, c.allowed + c.refused)}</td>
                    </tr>
                  ))}
                </tbody>
                {data.choices.length > 1 && (
                  <tfoot>
                    <tr>
                      <td>All purposes</td>
                      <td className="num">{number(choiceTotals.allowed)}</td>
                      <td className="num">{number(choiceTotals.refused)}</td>
                      <td className="num">{number(choiceTotals.withdrawn)}</td>
                      <td className="num">{pct(choiceTotals.allowed, choiceTotals.allowed + choiceTotals.refused)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </Card>
      </div>

      <div className="grid cols-2 ck-grid">
        <Card title="Declared cookies by purpose">
          {data.byCategory.length === 0 ? <Empty>No cookies declared.</Empty> : (
            <div className="table-scroll">
              <table className="data ck-table">
                <thead><tr><th>Purpose</th><th className="num">Cookies</th><th className="num">Share</th></tr></thead>
                <tbody>
                  {data.byCategory.map((c) => (
                    <tr key={c.purposeId ?? 'essential'}>
                      <td>{c.purpose}</td>
                      <td className="num">{number(c.cookies)}</td>
                      <td className="num">{pct(c.cookies, t.confirmed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Sites">
          {data.sites.length === 0 ? <Empty>No sites scanned.</Empty> : (
            <div className="table-scroll">
              <table className="data ck-table">
                <thead><tr><th>Site</th><th className="num">Cookies</th><th>Last scan</th></tr></thead>
                <tbody>
                  {data.sites.map((s) => (
                    <tr key={s.host}>
                      <td className="mono">{s.host}</td>
                      <td className="num">{number(s.cookies)}</td>
                      <td className="small">{s.lastScan ? day(s.lastScan) : <span className="muted">Never</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

/** Daily allowed and refused banner choices, stacked. Inline SVG, same form as the processor usage chart. */
function ChoiceTrend({ rows }: { rows: CookieDashboard['trend'] }) {
  if (!rows.some((r) => r.allowed || r.refused)) return <Empty>No banner choices in this period.</Empty>;
  const max = Math.max(1, ...rows.map((r) => r.allowed + r.refused));
  const width = 640; const height = 180; const left = 34; const right = 8; const top = 10; const bottom = 22;
  const plotH = height - top - bottom;
  const step = (width - left - right) / Math.max(1, rows.length);
  const y = (v: number) => top + plotH - (v / max) * plotH;
  const labelEvery = Math.max(1, Math.ceil(rows.length / 6));
  const allowed = rows.reduce((n, r) => n + r.allowed, 0);
  const refused = rows.reduce((n, r) => n + r.refused, 0);

  return (
    <div>
      <svg className="ck-chart" viewBox={`0 0 ${width} ${height}`} width="100%" role="img"
        aria-label={`Banner choices by day: ${allowed} allowed, ${refused} refused`}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={left} x2={width - right} y1={y(max * f)} y2={y(max * f)} stroke="var(--line-soft)" />
            <text x={left - 6} y={y(max * f) + 3} textAnchor="end" fontSize="10" fill="var(--ink-3)">{Math.round(max * f)}</text>
          </g>
        ))}
        {rows.map((r, i) => {
          const x = left + i * step + step * 0.15; const w = Math.max(1, step * 0.7);
          return (
            <g key={r.day}>
              <title>{`${day(r.day)} · ${r.allowed} allowed · ${r.refused} refused`}</title>
              <rect x={x} y={y(r.allowed + r.refused)} width={w} height={y(r.allowed) - y(r.allowed + r.refused)} fill="var(--cool)" />
              <rect x={x} y={y(r.allowed)} width={w} height={top + plotH - y(r.allowed)} fill="var(--ink)" />
            </g>
          );
        })}
        {rows.map((r, i) => (i % labelEvery === 0
          ? <text key={r.day} x={left + i * step + step / 2} y={height - 6} textAnchor="middle" fontSize="10" fill="var(--ink-3)">{shortDay(r.day)}</text>
          : null))}
      </svg>
      <div className="legend">
        <span className="key"><span className="swatch" style={{ background: 'var(--ink)' }} /> Allowed · {number(allowed)}</span>
        <span className="key"><span className="swatch" style={{ background: 'var(--cool)' }} /> Refused · {number(refused)}</span>
      </div>
    </div>
  );
}

function shortDay(d: string): string {
  return new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}
